import { useState } from "react";
import { ArrowUp, ArrowDown, MoreHorizontal, Copy, Power, History, FolderPlus, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { COLUMNS } from "../lib/columns";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function InlineCell({ value, onCommit, testid, prefix = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  if (!editing) {
    return (
      <span className="cursor-pointer hover:underline decoration-dotted"
            onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
            data-testid={testid}>
        {prefix ? money(value) : (value ?? 0)}
      </span>
    );
  }
  const commit = async () => {
    setEditing(false);
    const num = parseFloat(draft);
    if (!Number.isNaN(num) && num !== Number(value)) await onCommit(num);
  };
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
      className="h-6 w-20 rounded border border-[#007BFF] px-1 text-right text-xs"
      data-testid={`${testid}-input`}
    />
  );
}

/** Free-text inline edit — used for the item number, which is not a number. */
function InlineText({ value, onCommit, testid, placeholder = "—" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  if (!editing) {
    return (
      <span className="cursor-pointer decoration-dotted hover:underline"
            onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
            data-testid={testid}>
        {value || placeholder}
      </span>
    );
  }
  const commit = () => {
    setEditing(false);
    if (draft.trim() !== String(value ?? "")) onCommit(draft.trim());
  };
  return (
    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
           onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
           className="h-6 w-24 rounded border border-[#007BFF] px-1 text-xs"
           data-testid={`${testid}-input`} />
  );
}

/** Budget code picker. Amber when the item has no code or a code that no longer exists —
 *  those are the "bad code" / "no code" flags, fixable right here. */
function CodeCell({ item, costCodes, onInlineEdit }) {
  const known = costCodes.some((c) => c.id === item.cost_code_id);
  const broken = !item.cost_code_id || !known;
  return (
    <select value={known ? item.cost_code_id : ""}
            onChange={(e) => onInlineEdit(item, { cost_code_id: e.target.value || null })}
            onClick={(e) => e.stopPropagation()}
            className={`h-6 w-full max-w-[150px] rounded border px-1 text-[11px] ${
              broken ? "border-amber-300 bg-amber-50 text-amber-800"
                     : "border-transparent bg-transparent hover:border-slate-200"}`}
            title={broken ? "No valid budget code on this item — pick one" : item.cost_code}
            data-testid={`cell-cost-code-${item.id}`}>
      <option value="">{broken ? "— fix code —" : "— none —"}</option>
      {costCodes.map((c) => (
        <option key={c.id} value={c.id}>{c.code} {c.name}</option>
      ))}
    </select>
  );
}

function cellContent(item, key, onInlineEdit, costCodes, readOnly) {
  if (readOnly) {
    if (key === "unit_cost") return item.has_cost ? `$${Number(item.unit_cost || 0).toFixed(2)}` : "Quote";
    if (key === "cost_code" || key === "budget_code") return item.cost_code || "—";
    if (key === "item_number" || key === "labor_hours_per_unit") return item[key] ?? "—";
  }
  switch (key) {
    case "item_number":
      return <InlineText value={item.item_number} testid={`cell-item-number-${item.id}`}
                         onCommit={(v) => onInlineEdit(item, { item_number: v })} />;
    case "cost_code":
    case "budget_code":
      return <CodeCell item={item} costCodes={costCodes} onInlineEdit={onInlineEdit} />;
    case "unit_cost":
      return item.has_cost ? (
        <InlineCell value={item.unit_cost} prefix="$" testid={`cell-unit-cost-${item.id}`}
                    onCommit={(v) => onInlineEdit(item, { unit_cost: v })} />
      ) : (
        <span className="cursor-pointer rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
              onClick={() => onInlineEdit(item, { __open: true })}
              data-testid={`quote-badge-${item.id}`}>Quote</span>
      );
    case "labor_hours_per_unit":
      return <InlineCell value={item.labor_hours_per_unit} testid={`cell-labor-hrs-${item.id}`}
                        onCommit={(v) => onInlineEdit(item, { labor_hours_per_unit: v })} />;
    case "taxable":
      return item.taxable ? "Yes" : "No";
    case "flags":
      return (
        <div className="flex gap-1 flex-wrap">
          {Object.entries(item.flags || {}).filter(([, v]) => v).map(([f]) => (
            <span key={f} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{f}</span>
          ))}
        </div>
      );
    case "updated_at":
      return item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "—";
    default:
      return item[key] || (item[key] === 0 ? 0 : "—");
  }
}

export default function ItemGrid({
  items, visible, sort, direction, onSort, selected, toggleSelect, toggleSelectAll,
  onEdit, onDuplicate, onToggleActive, onHistory, onAddToGroup, onInlineEdit, onDelete,
  costCodes = [], readOnly = false,
}) {
  const cols = COLUMNS.filter((c) => visible.includes(c.key));
  const allSelected = items.length > 0 && items.every((i) => selected.includes(i.id));

  return (
    <div className="flex-1 overflow-auto rounded-lg border border-slate-200 bg-white">
      <table className="text-xs w-full" data-testid="price-list-table">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            <th className="w-8 px-2 py-2">
              {!readOnly && <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                     data-testid="select-all-checkbox" />}
            </th>
            {cols.map((c) => (
              <th key={c.key} style={{ minWidth: c.width }}
                  className={`px-3 py-2 font-medium text-slate-500 whitespace-nowrap ${
                    c.align === "right" ? "text-right" : "text-left"} ${
                    c.sortable === false ? "" : "cursor-pointer hover:text-slate-800"}`}
                  onClick={() => c.sortable !== false && onSort(c.key)}
                  data-testid={`col-header-${c.key}`}>
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  {sort === c.key && (direction === "asc"
                    ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                </span>
              </th>
            ))}
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={cols.length + 2} className="px-3 py-10 text-center text-slate-400">
              No items match these filters.
            </td></tr>
          )}
          {items.map((it) => (
            <tr key={it.id}
                className={`border-t border-slate-100 hover:bg-slate-50/70 ${it.active ? "" : "opacity-50"}`}
                data-testid={`price-list-row-${it.id}`}>
              <td className="px-2 py-1.5">
                {!readOnly && <input type="checkbox" checked={selected.includes(it.id)}
                       onChange={() => toggleSelect(it.id)}
                       data-testid={`row-checkbox-${it.id}`} />}
              </td>
              {cols.map((c) => (
                <td key={c.key}
                    className={`px-3 py-1.5 ${c.align === "right" ? "text-right" : ""} ${
                      c.mono ? "font-mono" : ""} ${c.key === "name" ? "text-slate-800" : "text-slate-600"}`}>
                  <div className={c.key === "flags" ? "" : "truncate"} style={{ maxWidth: c.width }}
                       title={typeof it[c.key] === "string" ? it[c.key] : undefined}>
                    {cellContent(it, c.key, onInlineEdit, costCodes, readOnly)}
                  </div>
                </td>
              ))}
              <td className="px-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded hover:bg-slate-200 text-slate-500"
                            data-testid={`row-actions-${it.id}`}>
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {!readOnly && (<>
                    <DropdownMenuItem onClick={() => onEdit(it)} data-testid={`action-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDuplicate(it)} data-testid={`action-duplicate-${it.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onToggleActive(it)} data-testid={`action-active-${it.id}`}>
                      <Power className="h-3.5 w-3.5 mr-2" /> {it.active ? "Deactivate" : "Reactivate"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAddToGroup(it)} data-testid={`action-add-group-${it.id}`}>
                      <FolderPlus className="h-3.5 w-3.5 mr-2" /> Add to Group
                    </DropdownMenuItem>
                    </>)}
                    <DropdownMenuItem onClick={() => onHistory(it)} data-testid={`action-history-${it.id}`}>
                      <History className="h-3.5 w-3.5 mr-2" /> Price History
                    </DropdownMenuItem>
                    {!readOnly && it.source === "manual" && (
                      <DropdownMenuItem onClick={() => onDelete(it)} className="text-red-600"
                                        data-testid={`action-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
